<script setup lang="ts">
import { ref, shallowRef, computed, onBeforeMount, onMounted, watch } from 'vue'
import { useCommonStore } from '~/stores/common'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useFetch, useRuntimeConfig } from 'nuxt/app' 
import { useSeoMeta } from '@unhead/vue'
import { definePageMeta, showError, useHead, onServerPrefetch } from '~/.nuxt/imports'
import { YandexMap, YandexMapDefaultSchemeLayer, YandexMapDefaultFeaturesLayer, YandexMapDefaultMarker, YandexMapMarker, YandexMapListener } from 'vue-yandex-maps'
import type { YMap } from '@yandex/ymaps3-types'
import Breadcrumbs from '~/components/Breadcrumbs.vue'

const commonStore = useCommonStore()
const config = useRuntimeConfig()

definePageMeta({
    layout: 'main',
    middleware: ['auth']
})

useSeoMeta({
    title: () => `Контактная информация — AKTIVO`,
    description: () => `Перейдя на страницу "Контакты" вы можете более детально ознакомиться с интересующей Вас информацией. Дополнительную информацию уточняйте по 📞: 8 (800) 500-64-64.`
})

const isWorkingTime = computed(() => {
    const now = new Date()
    const moscowTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Moscow" }))
    const hours = moscowTime.getHours()
    const dayOfWeek = moscowTime.getDay()
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5
    const isWorkingHours = hours >= 10 && hours < 19
    
    return isWeekday && isWorkingHours
})

const hasAutoRotate = ref(true)
const mapAzimuth = ref(0)
const map = shallowRef<YMap | null>(null)
const mapTilt = ref((40 * Math.PI) / 180)
const frame = ref<null | number>(null)

let contactData = ref<{ managers: { name: string, photo: string }[] } | null>(null)

const { data: contacts, error } = await useFetch<any>(`/api/about/aboutcontacts`)

if (contacts.value) {
    contactData.value = contacts.value
}

if (error.value) {
    showError({
        statusCode: error.value.statusCode,
        message: error.value.message
    })
}

const checkPageExists = () => {
    if (!contacts.value) {
        showError({
            statusCode: 404,
            statusMessage: 'Страница не найдена'
        })
    }
}

onServerPrefetch(() => {
    checkPageExists()
})

const startAutoRotationCamera = () => {
    if (!map.value) return

    mapAzimuth.value = map.value.azimuth + ((2 * Math.PI) / 180 / 100)
    frame.value = requestAnimationFrame(startAutoRotationCamera)
}

watch(map, () => {
    if (typeof requestAnimationFrame === 'undefined') return
    requestAnimationFrame(startAutoRotationCamera)
}, {
    immediate: true
})

onMounted(() => {
    if (frame.value) {
        hasAutoRotate.value = false
        cancelAnimationFrame(frame.value)
    }

    commonStore.setTheme('white')
    commonStore.setLoadingStatus(false)

    checkPageExists()
})
</script>

<template>
    <Breadcrumbs :data="[{ name: 'Контакты' }]" />
    <div class="contacts">
        <div class="container">
            <div class="contacts__wrapper">
                <div class="contacts__block">
                    <h1 class="contacts__title">Контакты</h1>
                    <div class="contacts__status">
                        <div class="contacts__status-icon" :class="[isWorkingTime ? 'active' : 'danger']"></div>
                        <span v-if="isWorkingTime">Сейчас работаем</span>
                        <span v-else>пн–пт: 10:00–19:00 (мск)</span>
                    </div>
                    <div class="contacts__items">
                        <div class="contacts__item">
                            <div class="contacts__item-title">ИНВЕСТОРАМ</div>
                            <a href="mailto:client@aktivo.ru" class="contacts__field">client@aktivo.ru</a>
                            <a href="tel:8 800 500-64-64" class="contacts__field">8 800 500-64-64</a>
                            <div class="contacts__field">
                                <a href="https://wa.me/79252118061" class="contacts__field-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <g clip-path="url(#clip0_3839_30527)">
                                            <path d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20Z" fill="#2DB742"/>
                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M13.9759 6.21573C12.9859 5.22573 11.6739 4.67773 10.2719 4.67773C7.38389 4.67773 5.03789 7.02573 5.02989 9.91173C5.02989 10.8377 5.26789 11.7377 5.72989 12.5297L4.98389 15.2437L7.76189 14.5177C8.52789 14.9357 9.38989 15.1537 10.2639 15.1537C13.1519 15.1537 15.4979 12.8057 15.5059 9.91173C15.5119 8.51573 14.9659 7.20373 13.9759 6.21373V6.21573ZM10.2719 14.2737C9.48789 14.2737 8.72189 14.0617 8.05389 13.6697L7.89389 13.5737L6.24789 14.0037L6.68589 12.3957L6.58389 12.2277C6.14589 11.5337 5.91589 10.7357 5.91589 9.91173C5.91589 7.51373 7.87189 5.55773 10.2699 5.55773C11.4339 5.55773 12.5279 6.01373 13.3499 6.83773C14.1739 7.66173 14.6239 8.75373 14.6239 9.91773C14.6299 12.3157 12.6759 14.2717 10.2759 14.2717L10.2719 14.2737ZM12.6579 11.0137C12.5299 10.9497 11.8859 10.6337 11.7639 10.5897C11.6419 10.5457 11.5579 10.5257 11.4679 10.6537C11.3779 10.7817 11.1279 11.0777 11.0559 11.1677C10.9779 11.2577 10.9019 11.2637 10.7739 11.1997C10.6459 11.1357 10.2199 10.9937 9.71989 10.5497C9.32789 10.2017 9.06989 9.77173 8.99389 9.64373C8.91789 9.51573 8.98789 9.44373 9.05189 9.37373C9.10989 9.31573 9.17989 9.21973 9.25189 9.14173C9.31589 9.06373 9.34189 9.01373 9.37989 8.92373C9.42589 8.83373 9.39989 8.76373 9.36789 8.69173C9.33589 8.62773 9.07189 7.98373 8.96189 7.71973C8.85189 7.46173 8.74989 7.50173 8.66589 7.49373C8.58789 7.48773 8.50589 7.48773 8.41589 7.48773C8.32589 7.48773 8.18389 7.51973 8.06789 7.64773C7.94589 7.77573 7.61189 8.09773 7.61189 8.74173C7.61189 9.38573 8.08189 10.0077 8.14589 10.0977C8.20989 10.1877 9.07189 11.5057 10.3839 12.0717C10.6999 12.2077 10.9379 12.2837 11.1299 12.3477C11.4459 12.4437 11.7279 12.4317 11.9539 12.3997C12.2039 12.3617 12.7259 12.0837 12.8359 11.7757C12.9459 11.4677 12.9459 11.2097 12.9139 11.1517C12.8819 11.1057 12.7979 11.0737 12.6639 11.0097L12.6579 11.0137Z" fill="white"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_3839_30527">
                                            <rect width="20" height="20" fill="white"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </a>
                                <a href="https://wa.me/79252118061" class="contacts__field-link">
                                    <div class="contacts__field-icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <rect width="20" height="20" rx="10" fill="#0E56A9"/>
                                            <path d="M12.8185 12.877C12.8689 12.877 12.894 12.94 12.8585 12.9766L10.0646 15.8516C10.0343 15.8825 9.98519 15.8825 9.95519 15.8516L8.65246 14.5108C8.62422 14.4814 8.61671 14.4366 8.63488 14.3995L9.35558 12.9327C9.37311 12.8974 9.40888 12.8751 9.44738 12.8751L12.8185 12.877ZM10.0421 6.54988C11.3319 5.30342 13.3876 5.31683 14.6603 6.5909C15.947 7.87923 15.947 9.96854 14.6603 11.2569L13.6564 12.2618C13.6253 12.2925 13.5727 12.2844 13.5528 12.2452L11.6573 8.50203C11.6294 8.44744 11.5733 8.41219 11.5118 8.41219H10.9044C10.8432 8.41219 10.7866 8.44744 10.7589 8.50203L8.04015 13.8399C8.02028 13.8789 7.96767 13.8872 7.93664 13.8565L5.33996 11.2569C4.05344 9.96853 4.05326 7.87917 5.33996 6.5909C6.61292 5.31667 8.66828 5.30336 9.95812 6.54988C9.98156 6.57271 10.0183 6.57259 10.0421 6.54988ZM11.0685 9.16023C11.0919 9.11349 11.1594 9.11339 11.1827 9.16023L12.6183 12.0333C12.6394 12.0754 12.6089 12.1251 12.5616 12.1251H9.68957C9.64229 12.1251 9.61083 12.0754 9.63195 12.0333L11.0685 9.16023Z" fill="#FBFBFB"/>
                                        </svg>
                                    </div>
                                    <span>Забота AKTIVO</span>
                                </a>
                                <a href="tg://resolve?domain=aktivo_official" class="contacts__field-link">
                                    <div class="contacts__field-icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <g clip-path="url(#clip0_3839_30549)">
                                                <path d="M10.3335 0C4.8135 0 0.333496 4.48 0.333496 10C0.333496 15.52 4.8135 20 10.3335 20C15.8535 20 20.3335 15.52 20.3335 10C20.3335 4.48 15.8535 0 10.3335 0ZM14.9735 6.8C14.8235 8.38 14.1735 12.22 13.8435 13.99C13.7035 14.74 13.4235 14.99 13.1635 15.02C12.5835 15.07 12.1435 14.64 11.5835 14.27C10.7035 13.69 10.2035 13.33 9.3535 12.77C8.3635 12.12 9.0035 11.76 9.5735 11.18C9.7235 11.03 12.2835 8.7 12.3335 8.49C12.3404 8.45819 12.3395 8.42517 12.3308 8.3938C12.3221 8.36244 12.3059 8.33367 12.2835 8.31C12.2235 8.26 12.1435 8.28 12.0735 8.29C11.9835 8.31 10.5835 9.24 7.8535 11.08C7.4535 11.35 7.0935 11.49 6.7735 11.48C6.4135 11.47 5.7335 11.28 5.2235 11.11C4.5935 10.91 4.1035 10.8 4.1435 10.45C4.1635 10.27 4.4135 10.09 4.8835 9.9C7.8035 8.63 9.7435 7.79 10.7135 7.39C13.4935 6.23 14.0635 6.03 14.4435 6.03C14.5235 6.03 14.7135 6.05 14.8335 6.15C14.9335 6.23 14.9635 6.34 14.9735 6.42C14.9635 6.48 14.9835 6.66 14.9735 6.8Z" fill="#27A7E7"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_3839_30549">
                                                <rect width="20" height="20" rx="10" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </div>
                                    <span>@aktivo_official</span>
                                </a>
                            </div>
                        </div>
                        <div class="contacts__item">
                            <div class="contacts__item-title">ПАРТНЕРАМ</div>
                            <a href="mailto:partners@aktivo.ru" class="contacts__field">partners@aktivo.ru</a>
                            <a href="tel:+7 (969) 777-83-92" class="contacts__field">+7 (969) 777-83-92</a>
                        </div>
                        <div class="contacts__item">
                            <div class="contacts__item-title">АРЕНДАТОРАМ</div>
                            <a href="tel:+7(912)012-11-71" class="contacts__field">+7 (912) 012-11-71</a>
                            <a href="mailto:arenda@aktivo.ru" class="contacts__field">arenda@aktivo.ru</a>
                            <div class="contacts__item-text">Попова Кристина Вячеславовна<br><i>Заместитель генерального директора по коммерции и маркетингу</i></div>
                        </div>
                        <div class="contacts__item">
                            <div class="contacts__item-title">ПРЕДЛОЖИТЬ СВОЙ ОБЪЕКТ</div>
                            <a href="mailto:realty@aktivo.ru" class="contacts__field">realty@aktivo.ru</a>
                        </div>
                        <div class="contacts__item">
                            <div class="contacts__item-title">СМИ</div>
                            <a href="mailto:press@aktivo.ru" class="contacts__field">press@aktivo.ru</a>
                            <div class="contacts__field">
                                <a href="tg://resolve?domain=aktivo_news" target="_blank" class="contacts__field-link">
                                    <div class="contacts__field-icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <g clip-path="url(#clip0_3839_30549)">
                                                <path d="M10.3335 0C4.8135 0 0.333496 4.48 0.333496 10C0.333496 15.52 4.8135 20 10.3335 20C15.8535 20 20.3335 15.52 20.3335 10C20.3335 4.48 15.8535 0 10.3335 0ZM14.9735 6.8C14.8235 8.38 14.1735 12.22 13.8435 13.99C13.7035 14.74 13.4235 14.99 13.1635 15.02C12.5835 15.07 12.1435 14.64 11.5835 14.27C10.7035 13.69 10.2035 13.33 9.3535 12.77C8.3635 12.12 9.0035 11.76 9.5735 11.18C9.7235 11.03 12.2835 8.7 12.3335 8.49C12.3404 8.45819 12.3395 8.42517 12.3308 8.3938C12.3221 8.36244 12.3059 8.33367 12.2835 8.31C12.2235 8.26 12.1435 8.28 12.0735 8.29C11.9835 8.31 10.5835 9.24 7.8535 11.08C7.4535 11.35 7.0935 11.49 6.7735 11.48C6.4135 11.47 5.7335 11.28 5.2235 11.11C4.5935 10.91 4.1035 10.8 4.1435 10.45C4.1635 10.27 4.4135 10.09 4.8835 9.9C7.8035 8.63 9.7435 7.79 10.7135 7.39C13.4935 6.23 14.0635 6.03 14.4435 6.03C14.5235 6.03 14.7135 6.05 14.8335 6.15C14.9335 6.23 14.9635 6.34 14.9735 6.42C14.9635 6.48 14.9835 6.66 14.9735 6.8Z" fill="#27A7E7"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_3839_30549">
                                                <rect width="20" height="20" rx="10" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </div>
                                    <span>aktivo_news</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="contacts__block">
                    <div class="contacts__top">
                        <h2 class="contacts__title">Офис в москве</h2>
                        <div class="contacts__team" v-if="contactData?.managers">
                            <div class="contacts__team-image" v-for="manager in contactData.managers">
                                <NuxtImg
                                    :src="`${config.public.MAIN_URL}${manager.photo}`"
                                    :alt="`${manager.name}`"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                        <div class="contacts__text">123022, Москва, ул. 2-я Звенигородская, д. 13с42, 8 этаж</div>
                        <a href="https://yandex.com/maps/213/moscow/?from=api-maps&ll=37.551717%2C55.761355&mode=routes&origin=jsapi_2_1_79&rtext=~55.761355%2C37.551717&rtt=auto&ruri=~ymapsbm1%3A%2F%2Fgeo%3Fdata%3DIgoN9TQWQhWhC19C&z=15" target="_blank" class="contacts__button btn btn--secondary">
                            <div class="btn__arrow left">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7.262 19.7281C7.262 19.7281 0 13.7354 0 7.83872C0 5.75976 0.842855 3.76595 2.34315 2.29591C3.84344 0.825863 5.87827 0 8 0C10.1217 0 12.1566 0.825863 13.6569 2.29591C15.1571 3.76595 16 5.75976 16 7.83872C16 13.7354 8.738 19.7281 8.738 19.7281C8.334 20.0926 7.669 20.0887 7.262 19.7281ZM8 11.2682C8.45963 11.2682 8.91475 11.1795 9.33939 11.0071C9.76403 10.8348 10.1499 10.5821 10.4749 10.2637C10.7999 9.94524 11.0577 9.56719 11.2336 9.15111C11.4095 8.73503 11.5 8.28908 11.5 7.83872C11.5 7.38836 11.4095 6.94241 11.2336 6.52633C11.0577 6.11025 10.7999 5.73219 10.4749 5.41374C10.1499 5.09529 9.76403 4.84267 9.33939 4.67033C8.91475 4.49798 8.45963 4.40928 8 4.40928C7.07174 4.40928 6.1815 4.77059 5.52513 5.41374C4.86875 6.05688 4.5 6.92917 4.5 7.83872C4.5 8.74826 4.86875 9.62055 5.52513 10.2637C6.1815 10.9068 7.07174 11.2682 8 11.2682Z" fill="#2A415E"/>
                                </svg>
                            </div>
                            <span>Построить маршрут</span>
                        </a>
                    </div>
                    <div class="contacts__map">
                        <yandex-map
                            v-model="map"
                            :height="`100%`"
                            :settings="{
                                location: {
                                    center: [37.551716999999975, 55.76135556896124],
                                    zoom: 16
                                },
                                camera: { azimuth: mapAzimuth, tilt: mapTilt, duration: hasAutoRotate ? 0 : 250 },
                                showScaleInCopyrights: true
                            }"
                            :width="`100%`"
                        >
                            <yandex-map-default-scheme-layer :settings="{ theme: 'dark' }" />
                            <yandex-map-default-features-layer />
                            <yandex-map-listener :settings="{ onActionStart: () => hasAutoRotate = false }"/>
                            <yandex-map-marker
                                :settings="{
                                    coordinates: [37.551716999999975, 55.76135556896124]
                                }"
                            >
                                <div class="marker">
                                    <svg width="43" height="55" viewBox="0 0 43 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21.5 0C27.2022 5.03944e-07 32.6711 2.25965 36.7031 6.28223C40.735 10.3047 42.9999 15.7606 43 21.4492C43 37.5847 23.4834 53.9834 23.4834 53.9834C22.3977 54.9807 20.6104 54.97 19.5166 53.9834C19.5166 53.9834 0 37.5847 0 21.4492C9.61972e-05 15.7606 2.26497 10.3047 6.29688 6.28223C10.3289 2.25965 15.7978 0 21.5 0Z" fill="#FFCE00"/>
                                        <path d="M20.2663 13.9717L9.31519 29.1089H12.6405L15.0408 25.7535H28.0921L30.4923 29.1089H33.7676L22.8665 13.9717H20.2663ZM26.2919 23.2054H16.8409L21.5664 16.5955L26.2919 23.2054Z" fill="white"/>
                                    </svg>
                                </div>
                            </yandex-map-marker>
                        </yandex-map>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!--noindex-->
    <!--89697778392-->
    <!--89581004465-->
    <!--89697770936-->
    <!--89581003012-->
    <!--89326989623-->
    <!--89668880646-->
    <!--89697776489-->
    <!--89668880365-->
    <!--89697778266-->
    <!--84950215709-->
    <!--84950322390-->
    <!--84950196339-->
    <!--89668889127-->
    <!--89254545877-->
    <!--84950676718-->
    <!--84951527764-->
    <!--84950676221-->
    <!--84951526895-->
    <!--89770905300-->
    <!--89770905303-->
    <!--89770905343-->
    <!--/noindex-->
</template>

<style lang="scss" scoped>
.contacts {
    padding-top: 119px;
    margin-top: -119px;
    background-color: #E1E5EA;

    &__block {
        padding: 24px;
        background: white;
        border-radius: 24px;
        margin-bottom: 16px;
        overflow: hidden;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__title {
        color: #162133;
        font-family: 'Muller', sans-serif;
        font-size: 20px;
        font-weight: 400;
        line-height: 120%;
        text-transform: uppercase;
        margin-bottom: 16px;
    }

    &__status {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        color: #6A7784;
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 140%; 
        margin-bottom: 16px;
    }

    &__status-icon {
        width: 8px;
        height: 8px;
        margin-right: 8px;
        border-radius: 50%;

        &.active {
            background: #1CA242;
        }

        &.danger {
            background: #E53838;
        }
    }

    &__items {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        flex-wrap: wrap;
    }

    &__item {
        align-self: stretch;
        width: calc(50% - 8px);
        margin-bottom: 16px;

        &:nth-last-child(-n+2) {
            margin-bottom: 0;
        }
    }

    &__item-title {
        color: #71AAEB;
        font-size: 12px;
        font-weight: 600;
        line-height: normal;
        margin-bottom: 8px;
    }

    &__item-text {
        color: #6A7784;
        font-size: 12px;
        font-weight: 500;
        line-height: 140%;
        font-weight: 600;

        i {
            font-weight: 400;
        }
    }

    &__field {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        flex-wrap: wrap;
        margin-bottom: 8px;
        color: #162133;
        font-size: 12px;
        font-weight: 500;
        line-height: 140%; 
        text-decoration: none;
        transition: all 0.2s ease;

        &:last-child {
            margin-bottom: 0;
        }

        &:hover {
            opacity: 0.8;
        }
    }

    &__field-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 4px;
        text-decoration: none;
    }

    &__field-link {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        color: #162133;
        font-size: 12px;
        font-weight: 500;
        line-height: 140%; 
        text-decoration: none;

        &:last-child {
            width: 100%;
            margin-top: 8px;
        }
    }

    &__top {
        margin-bottom: 16px;
    }

    &__text {
        color: #6A7784;
        font-size: 12px;
        font-weight: 500;
        line-height: normal;
        margin-bottom: 16px;
    }

    &__team {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding-top: 8px;
        margin-bottom: 16px;
    }

    &__team-image {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        border: 4px solid white;
        overflow: hidden;

        &:not(&:first-child) {
            margin-left: -16px;
        }

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: top;
        }
    }

    &__map {
        height: 400px;
        width: calc(100% + 48px);
        margin: 0 -24px -24px;

        .marker {
            position: relative;
            width: 43px;
            height: 55px;
            display: flex;
            align-items: center;
            justify-content: center;
            transform: translateX(-50%) translateY(-50%) scale(1.5);
        }
    }
}

@media screen and (min-width: 767.98px) {
    .contacts {
        &__block {
            border-radius: 32px;
            padding: 32px;
            margin-bottom: 24px;
        }

        &__title {
            font-size: 32px;
            line-height: 120%;
        }

        &__field {
            font-size: 16px;
            line-height: 140%; 
        }

        &__field-link {
            font-size: 16px;
            line-height: 140%; 
        }

        &__top {
            margin-bottom: 32px;
        }

        &__text {
            font-size: 16px;
            line-height: normal;
            margin-bottom: 24px;
        }

        &__team {
            padding-top: 16px;
        }

        &__team-image {
            width: 60px;
            height: 60px;
        }

        &__map {
            margin: 0 -32px -32px;
            width: calc(100% + 64px);
        }
    }
}

@media screen and (min-width: 1199.98px) {
    .contacts {
        &__block {
            padding: 48px;
            margin-bottom: 56px;

            &:nth-child(2) {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
            }
        }

        &__status {
            margin-bottom: 32px;
        }

        &__item {
            width: calc(20% - 8px);
            margin-bottom: 0;
        }

        &__top {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            width: 425px;
            margin-bottom: 0;
            padding: 48px;
            align-self: stretch;
        }

        &__button {
            padding: 0 30px;
            height: 54px;
            font-size: 16px;
            line-height: 140%;
        }

        &__team {
            padding-top: 8px;
        }

        &__team-image {
            width: 80px;
            height: 80px;
        }

        &__map {
            width: calc(100% - 425px);
            height: 380px;
            margin: 0
        }
    }
}

@media screen and (min-width: 1360px) {
    .contacts {
        padding-top: 165px;
        margin-top: -165px;

        &__title {
            font-size: 34px;
        }
    }
}
</style>